import { useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { BookOpen, Download, Rocket, RotateCcw, Star } from "lucide-react";

const playSound = (type) => {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    const ctx = window.__lwbAudio || (window.__lwbAudio = new Ctx());
    if (ctx.state === "suspended") ctx.resume();
    const notes = {
      pop: [[520, 0, 0.12, "triangle"]],
      boop: [[196, 0, 0.18, "sawtooth"], [155, 0.16, 0.22, "sawtooth"]],
      fanfare: [[523, 0, 0.16, "triangle"], [659, 0.16, 0.16, "triangle"], [784, 0.32, 0.16, "triangle"], [1047, 0.48, 0.4, "triangle"]],
    }[type];
    notes.forEach(([f, t, d, wave]) => {
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.type = wave;
      o.frequency.value = f;
      g.gain.setValueAtTime(0.001, ctx.currentTime + t);
      g.gain.exponentialRampToValueAtTime(0.08, ctx.currentTime + t + 0.02);
      g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + t + d);
      o.connect(g).connect(ctx.destination);
      o.start(ctx.currentTime + t);
      o.stop(ctx.currentTime + t + d + 0.05);
    });
  } catch {}
};

const playYay = () => {
  try {
    const store = window.__lwbVoices || (window.__lwbVoices = {});
    const a = store.yay || (store.yay = new Audio("/audio/yay.mp3"));
    a.currentTime = 0;
    a.volume = 0.4;
    a.play().catch(() => {});
  } catch {}
};

const CHARACTERS = [
  { id: "brave", name: "Bailey", img: "/images/affirm-brave.png" },
  { id: "blessed", name: "Blessed friend", img: "/images/affirm-blessed.png" },
  { id: "happy", name: "Happy friend", img: "/images/affirm-happy.png" },
  { id: "thankful", name: "Thankful friend", img: "/images/affirm-thankful.png" },
];

const ROUNDS = [
  { phrase: "I AM BRAVE", correct: "brave", cheer: "Showing courage every day, in every way!" },
  { phrase: "I AM BLESSED", correct: "blessed", cheer: "A good and divine gift!" },
  { phrase: "I AM HAPPY", correct: "happy", cheer: "Feeling joy and showing it!" },
  { phrase: "I AM THANKFUL", correct: "thankful", cheer: "Feeling good for everything!" },
];

const INTROS = ["Affirmation one…", "Affirmation two…", "Affirmation three…", "Last affirmation…"];

const BADGE_IMG = "/images/badge-affirmation.png";

const BURST = [
  { x: -140, y: -90, c: "#FFE9A8" }, { x: 140, y: -100, c: "#A8D5A2" }, { x: -180, y: 20, c: "#BFE0F7" },
  { x: 180, y: 30, c: "#F5B8A6" }, { x: -80, y: -160, c: "#F5B8A6" }, { x: 90, y: -170, c: "#FFE9A8" },
  { x: -60, y: 120, c: "#A8D5A2" }, { x: 70, y: 130, c: "#BFE0F7" }, { x: 0, y: -190, c: "#4FA3E3" },
  { x: -190, y: -40, c: "#FFE9A8" }, { x: 190, y: -50, c: "#A8D5A2" }, { x: 0, y: 170, c: "#F5B8A6" },
];

const FIREWORKS = [
  { x: "10%", y: "16%", c: "#F5A623", delay: 0 },
  { x: "88%", y: "12%", c: "#4FA3E3", delay: 0.5 },
  { x: "76%", y: "42%", c: "#71BE6B", delay: 1.0 },
  { x: "18%", y: "52%", c: "#E8564A", delay: 1.4 },
  { x: "50%", y: "8%", c: "#C78A0F", delay: 0.8 },
];

const FireworkBurst = ({ x, y, c, delay }) => (
  <div className="firework" style={{ left: x, top: y }}>
    <span className="firework-flash" style={{ animationDelay: `${delay}s` }} />
    {Array.from({ length: 12 }).map((_, i) => {
      const a = (i / 12) * Math.PI * 2;
      const d = 70 + (i % 3) * 26;
      return (
        <span
          key={i}
          className="firework-dot"
          style={{
            background: i % 4 === 3 ? "#FFFDF2" : c,
            animationDelay: `${delay}s`,
            "--dx": `${Math.cos(a) * d}px`,
            "--dy": `${Math.sin(a) * d}px`,
          }}
        />
      );
    })}
  </div>
);

const Streamer = ({ color, style, delay = 0 }) => (
  <svg className="streamer" style={{ ...style, animationDelay: `${delay}s` }} width="26" height="120" viewBox="0 0 26 120">
    <path
      d="M13 0 C 24 14 2 24 13 38 C 24 52 2 62 13 76 C 24 90 2 100 13 116"
      fill="none"
      stroke={color}
      strokeWidth="6"
      strokeLinecap="round"
    />
  </svg>
);

const shuffled = () => [...CHARACTERS].sort(() => Math.random() - 0.5);

export default function AffirmationGame() {
  const [stage, setStage] = useState("welcome");
  const [round, setRound] = useState(0);
  const [cards, setCards] = useState(shuffled);
  const [shakeId, setShakeId] = useState(null);
  const [oops, setOops] = useState(false);
  const [best, setBest] = useState(() => Number(localStorage.getItem("lwb-affirm-stars") || 0));

  const stars = stage === "complete" ? ROUNDS.length : stage === "cheer" ? round + 1 : round;

  const start = () => {
    playSound("pop");
    setCards(shuffled());
    setRound(0);
    setStage("challenge");
  };

  const pick = (c) => {
    if (c.id === ROUNDS[round].correct) {
      playSound("fanfare");
      const n = Math.max(best, round + 1);
      localStorage.setItem("lwb-affirm-stars", String(n));
      setBest(n);
      setStage("cheer");
    } else {
      playSound("boop");
      setOops(true);
      setShakeId(c.id + Date.now());
      setTimeout(() => setOops(false), 2400);
    }
  };

  const next = () => {
    playSound("pop");
    if (round < ROUNDS.length - 1) {
      setRound((i) => i + 1);
      setCards(shuffled());
      setStage("challenge");
    } else {
      playYay();
      setStage("complete");
    }
  };

  return (
    <div
      data-testid="affirmation-game"
      className="min-h-screen relative overflow-hidden flex flex-col"
      style={{ background: "linear-gradient(180deg, #FFF0BE 0%, #FFF8DC 55%, #FFFDF2 100%)" }}
    >
      <svg className="cloud cloud-a" viewBox="0 0 80 40" width="90" height="45">
        <ellipse cx="28" cy="28" rx="22" ry="11" fill="#FFFFFF" />
        <ellipse cx="48" cy="22" rx="18" ry="12" fill="#FFFFFF" />
      </svg>
      <svg className="cloud cloud-b" viewBox="0 0 80 40" width="64" height="32">
        <ellipse cx="26" cy="28" rx="20" ry="10" fill="#FFFFFF" />
        <ellipse cx="44" cy="22" rx="16" ry="11" fill="#FFFFFF" />
      </svg>

      <header className="relative z-20 flex items-center justify-between px-5 sm:px-8 py-4">
        <Link to="/" data-testid="affirm-home-link" className="flex items-center gap-2 opacity-80 hover:opacity-100 transition-opacity">
          <img src="/images/lwb-logo.png" alt="Learn With Bailey" className="h-9 w-auto" />
        </Link>
        {best > 0 && (
          <span data-testid="stars-chip" className="rounded-full bg-amberbrand text-cream text-xs font-bold uppercase tracking-widest px-4 py-2 shadow inline-flex items-center gap-1.5">
            <Star size={13} fill="currentColor" /> Stars {best}/4
          </span>
        )}
      </header>

      <main className="relative z-10 flex-1 flex items-center justify-center px-4 sm:px-6 pb-16">
        <AnimatePresence mode="wait">
          {stage === "welcome" && (
            <motion.div
              key="welcome"
              data-testid="affirm-welcome"
              className="text-center max-w-2xl"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0, y: -30 }}
            >
              <motion.img
                src="/images/affirm-brave.png"
                alt="Superhero Bailey"
                data-testid="affirm-hero-character"
                className="w-44 sm:w-56 mx-auto mb-2 drop-shadow-[0_18px_24px_rgba(49,67,92,0.35)]"
                initial={{ y: 120, opacity: 0, scale: 0.6 }}
                animate={{ y: 0, opacity: 1, scale: 1 }}
                transition={{ type: "spring", stiffness: 120, damping: 11, delay: 0.2 }}
              />
              <motion.h1
                className="font-display font-extrabold tracking-tight text-4xl sm:text-6xl text-walnut"
                initial={{ y: 30, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.45, duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
              >
                I AM…
              </motion.h1>
              <motion.div
                className="mt-5 bg-white/85 border-2 border-sand rounded-3xl px-7 py-5 shadow-lg inline-block text-left"
                initial={{ y: 30, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.7, duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
              >
                <p className="font-display font-bold text-xl sm:text-2xl text-walnut">The Affirmation Game!</p>
                <p className="text-base sm:text-lg text-walnutsoft mt-1">I'll show you an affirmation.</p>
                <p className="text-base sm:text-lg text-walnutsoft">Tap the friend it belongs to!</p>
              </motion.div>
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: "spring", stiffness: 160, damping: 10, delay: 1.05 }}
              >
                <motion.button
                  data-testid="affirm-start-button"
                  onClick={start}
                  className="mt-8 rounded-full bg-terracotta text-cream font-display font-extrabold text-xl sm:text-2xl px-12 py-5 inline-flex items-center gap-3 shadow-[0_20px_40px_-12px_rgba(79,163,227,0.8)]"
                  animate={{ scale: [1, 1.06, 1] }}
                  transition={{ repeat: Infinity, duration: 1.6, ease: "easeInOut" }}
                  whileHover={{ scale: 1.12 }}
                  whileTap={{ scale: 0.94 }}
                >
                  LET'S GO! <Rocket size={26} />
                </motion.button>
              </motion.div>
            </motion.div>
          )}

          {stage === "challenge" && (
            <motion.div
              key={`challenge-${round}`}
              data-testid="affirm-challenge"
              className="text-center max-w-4xl w-full"
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -30 }}
            >
              <p className="font-mono text-sm sm:text-base uppercase tracking-[0.25em] text-amberbrand mb-3">
                {INTROS[round]}
              </p>
              <div data-testid="affirm-progress" className="flex items-center justify-center gap-2.5 mb-6">
                {ROUNDS.map((_, i) => (
                  <motion.span
                    key={i}
                    animate={i < stars ? { scale: [1, 1.4, 1], rotate: [0, 18, 0] } : {}}
                    transition={{ duration: 0.6 }}
                  >
                    <Star
                      size={26}
                      className={i < stars ? "text-amberbrand" : "text-walnut/20"}
                      fill={i < stars ? "currentColor" : "none"}
                    />
                  </motion.span>
                ))}
              </div>
              <h2
                data-testid="affirm-phrase"
                className="font-display font-extrabold tracking-tight text-4xl sm:text-6xl text-walnut mb-10"
              >
                {ROUNDS[round].phrase}
              </h2>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6 max-w-4xl mx-auto">
                {cards.map((c, i) => (
                  <motion.button
                    key={c.id}
                    data-testid={`affirm-card-${c.id}`}
                    onClick={() => pick(c)}
                    className="group bg-white/85 border-2 border-sand rounded-3xl p-4 sm:p-6 shadow-lg cursor-pointer"
                    initial={{ y: 60, opacity: 0 }}
                    animate={
                      shakeId && shakeId.startsWith(c.id)
                        ? { x: [0, -12, 12, -9, 9, 0], y: 0, opacity: 1 }
                        : { y: 0, opacity: 1, x: 0 }
                    }
                    transition={{ delay: i * 0.12, type: "spring", stiffness: 140, damping: 12 }}
                    whileHover={{ scale: 1.06, rotate: -1.5 }}
                    whileTap={{ scale: 0.93 }}
                  >
                    <img src={c.img} alt={c.name} className="w-full h-36 sm:h-48 object-contain" />
                  </motion.button>
                ))}
              </div>
              <AnimatePresence>
                {oops && (
                  <motion.p
                    data-testid="affirm-try-again"
                    className="mt-8 inline-block bg-white/85 border-2 border-terracotta/40 rounded-full px-6 py-3 font-display font-bold text-walnut"
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0 }}
                  >
                    Not that friend — try again, star!
                  </motion.p>
                )}
              </AnimatePresence>
            </motion.div>
          )}

          {stage === "cheer" && (
            <motion.div
              key={`cheer-${round}`}
              data-testid="affirm-cheer"
              className="text-center max-w-2xl relative"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0, y: -30 }}
            >
              {BURST.map((s, i) => (
                <motion.span
                  key={i}
                  className="absolute left-1/2 top-24 text-2xl pointer-events-none"
                  style={{ color: s.c }}
                  initial={{ x: 0, y: 0, scale: 0, opacity: 1 }}
                  animate={{ x: s.x, y: s.y, scale: [0, 1.4, 1], opacity: [1, 1, 0] }}
                  transition={{ duration: 1.4, delay: 0.1 + i * 0.04, ease: "easeOut" }}
                >
                  ✦
                </motion.span>
              ))}
              <motion.img
                src={CHARACTERS.find((c) => c.id === ROUNDS[round].correct).img}
                alt={ROUNDS[round].phrase}
                data-testid="cheer-character"
                className="w-44 sm:w-60 mx-auto drop-shadow-[0_18px_24px_rgba(49,67,92,0.35)]"
                initial={{ scale: 0, rotate: -12 }}
                animate={{ scale: 1, rotate: 0 }}
                transition={{ type: "spring", stiffness: 130, damping: 9 }}
              />
              <motion.h2
                className="mt-4 font-display font-extrabold tracking-tight text-4xl sm:text-6xl text-walnut"
                initial={{ scale: 0.5, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ type: "spring", stiffness: 150, damping: 10, delay: 0.15 }}
              >
                {ROUNDS[round].phrase}!
              </motion.h2>
              <motion.p
                className="mt-3 font-display font-bold text-lg sm:text-2xl text-walnutsoft"
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
              >
                {ROUNDS[round].cheer}
              </motion.p>
              <motion.div
                className="mt-5 flex items-center justify-center gap-2.5"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.65 }}
              >
                {ROUNDS.map((_, i) => (
                  <motion.span
                    key={i}
                    animate={i === stars - 1 ? { scale: [0, 1.6, 1], rotate: [0, 25, 0] } : {}}
                    transition={{ duration: 0.7, type: "spring", stiffness: 200, damping: 10 }}
                  >
                    <Star
                      size={30}
                      className={i < stars ? "text-amberbrand" : "text-walnut/20"}
                      fill={i < stars ? "currentColor" : "none"}
                    />
                  </motion.span>
                ))}
              </motion.div>
              <motion.div
                className="mt-8"
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.85 }}
              >
                <button
                  data-testid="affirm-next-button"
                  onClick={next}
                  className="rounded-full bg-terracotta text-cream font-display font-extrabold text-lg sm:text-xl px-10 py-4 inline-flex items-center gap-2 hover:bg-terracottadark transition-colors shadow-[0_16px_32px_-12px_rgba(79,163,227,0.7)]"
                >
                  {round < ROUNDS.length - 1 ? (
                    <>Next affirmation <Rocket size={20} /></>
                  ) : (
                    <>Earn my badge <Star size={20} /></>
                  )}
                </button>
              </motion.div>
            </motion.div>
          )}

          {stage === "complete" && (
            <motion.div
              key="complete"
              data-testid="affirm-complete"
              className="text-center max-w-2xl relative"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
            >
              <div className="fixed inset-0 pointer-events-none z-0 overflow-hidden">
                {FIREWORKS.map((f, i) => (
                  <FireworkBurst key={i} {...f} />
                ))}
                <Streamer color="#E8564A" style={{ left: "6%" }} delay={0} />
                <Streamer color="#4FA3E3" style={{ left: "12%" }} delay={0.7} />
                <Streamer color="#71BE6B" style={{ right: "9%" }} delay={0.4} />
                <Streamer color="#F5A623" style={{ right: "4%" }} delay={1.1} />
                <Streamer color="#F5B8A6" style={{ left: "48%" }} delay={0.9} />
              </div>
              <motion.h2
                className="font-display font-extrabold tracking-tight text-4xl sm:text-6xl text-walnut"
                initial={{ scale: 0.5, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ type: "spring", stiffness: 150, damping: 10 }}
              >
                AFFIRMATION STAR!
              </motion.h2>
              <motion.div
                className="my-6 inline-block"
                initial={{ scale: 0, rotate: -30 }}
                animate={{ scale: 1, rotate: 0 }}
                transition={{ type: "spring", stiffness: 120, damping: 8, delay: 0.35 }}
              >
                <img src={BADGE_IMG} alt="Affirmation Star badge" data-testid="affirmation-badge" className="w-44 sm:w-56 drop-shadow-[0_20px_30px_rgba(49,67,92,0.35)]" />
              </motion.div>
              <motion.p
                className="font-display font-bold text-xl sm:text-2xl text-walnut"
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 }}
              >
                You matched all four affirmations — say them with me:
                <br />
                <span className="text-amberbrand">I am brave · I am blessed · I am happy · I am thankful!</span>
              </motion.p>
              <motion.div
                className="mt-10 flex flex-wrap justify-center gap-4"
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.0 }}
              >
                <button
                  data-testid="affirm-play-again"
                  onClick={start}
                  className="rounded-full border-2 border-walnut text-walnut font-display font-bold text-base px-8 py-4 inline-flex items-center gap-2 hover:bg-walnut hover:text-cream transition-colors"
                >
                  <RotateCcw size={18} /> Play again
                </button>
                <a
                  href="/downloads/affirmations-i-am-poster.pdf"
                  download="Baileys-Affirmations-I-Am-Poster.pdf"
                  data-testid="affirm-download-poster"
                  className="rounded-full bg-amberbrand text-cream font-display font-bold text-base px-8 py-4 inline-flex items-center gap-2 hover:brightness-95 transition shadow-[0_16px_32px_-12px_rgba(199,138,15,0.6)]"
                >
                  <Download size={18} /> Download the I am Affirmations gift
                </a>
                <Link
                  to="/books/baileys-affirmations"
                  data-testid="affirm-get-book"
                  className="rounded-full bg-terracotta text-cream font-display font-bold text-base px-8 py-4 inline-flex items-center gap-2 hover:bg-terracottadark transition-colors shadow-[0_16px_32px_-12px_rgba(79,163,227,0.7)]"
                >
                  <BookOpen size={18} /> Get the Bailey's Affirmations book
                </Link>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </main>

      <div className="relative z-0 h-10 bg-sagecream border-t-4 border-dashed border-sand" />
    </div>
  );
}
